<script setup lang="ts">
import { computed, reactive, ref, Ref, watch } from 'vue';
import { useColor } from '@/composables/color';
import { useClickOutside } from '@/composables/clickOutside';
import ColorSelector from '../ColorSelector.vue';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import type { FontColorOption, ImagePlaceholderElement, WordCloudConfig } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';

const DEFAULT_BG_COLOR: ColorObject = { hex: '#000000', alpha: 1, cmyk: '' };

const COLOR_MODES: CButtonGroupItem<boolean>[] = [
    {
        label: 'Use image colors',
        value: true
    },
    {
        label: 'Use default colors',
        value: false
    }
];

const BG_MODES: CButtonGroupItem<boolean>[] = [
    {
        label: 'Transparent Background',
        value: true
    },
    {
        label: 'Choose Color',
        value: false
    }
];

const props = defineProps<{
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
}>();

const disabled = computed(() => !!props.imageElement?.locked);

const model = reactive({
    useImageColors: true,
    transparentBackground: true,
    bgColor: undefined as ColorObject | undefined
});

const { getColorFromString, hexToHexa } = useColor();
watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.wordCloudConfig) return;

        const config = element.wordCloudConfig;
        model.useImageColors = config.useImageColors ?? true;
        model.transparentBackground = !config.bgColor;
        model.bgColor = undefined;

        if (config.bgColor) {
            const color = getColorFromString(config.bgColor);
            model.bgColor = {
                hex: color.hex,
                alpha: color.a,
                cmyk: ''
            }
        }

    },
    { immediate: true }
);


const pickerRef: Ref<HTMLElement | undefined> = ref();
const { clickOutsideActive } = useClickOutside(pickerRef, () => toggleColorSelection(), false);
const toggleColorSelection = () => {
    clickOutsideActive.value = !clickOutsideActive.value;
};

const onBgModeChanged = () => {
    if (!model.transparentBackground) {
        onBgColorSelected(DEFAULT_BG_COLOR);
        clickOutsideActive.value = false;
        return;
    }
    model.bgColor = undefined;
    updateWordCloudConfig({ bgColor: undefined });
};

const onBgColorSelected = (color: FontColorOption) => {
    model.bgColor = { ...color } as ColorObject;
    updateWordCloudConfig({ bgColor: hexToHexa(model.bgColor.hex, model.bgColor.alpha) });
    toggleColorSelection();
};


const updateWordCloudConfig = (config: Partial<WordCloudConfig>) => {
    const element = props.imageElement;
    if (!element) return;

    const newConfig = { ...element.wordCloudConfig, ...config } as WordCloudConfig;
    element.set('wordCloudConfig', newConfig);
    if (props.editMode) element.setEditedProp('wordCloudConfig');
};
</script>

<template>
    <div class="word-cloud-color-wrapper">
        <section class="section">
            <h6>Words</h6>
            <div class="border-bottom" />
            <c-button-group
                v-model="model.useImageColors"
                no-icon
                type="radio"
                wide
                :items="COLOR_MODES"
                :disabled="disabled"
                @input="updateWordCloudConfig({ useImageColors: model.useImageColors })"
            />
        </section>
        <section class="section">
            <h6>Background</h6>
            <div class="border-bottom" />
            <c-button-group
                v-model="model.transparentBackground"
                no-icon
                type="radio"
                wide
                :items="BG_MODES"
                :disabled="disabled"
                @input="onBgModeChanged"
            />
            <div
                v-if="!model.transparentBackground && model.bgColor"
                class="color-row border-gray-light rounded"
                @click="toggleColorSelection"
            >
                <span class="color-preview" :style="{ backgroundColor: model.bgColor.hex }" />
                {{ model.bgColor.hex }}
            </div>
        </section>
        <color-selector
            ref="pickerRef"
            :open="clickOutsideActive"
            :color="model.bgColor"
            @cancel="toggleColorSelection"
            @color-selected="onBgColorSelected"
        />
    </div>
</template>

<style scoped lang="scss">
.word-cloud-color-wrapper {
    .section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin-bottom: 0;
        }
        &:is(:first-child) {
            margin-bottom: 0.625rem;
        }
        .color-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.625rem;
            cursor: pointer;
            .color-preview {
                width: 1.25rem;
                height: 1.25rem;
                border-radius: 0.125rem;
            }
        }
    }
}
</style>
